import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { ReactComponent as ImpersonateIcon } from "assets/ImpersonateButtonIcon.svg";
import { setImpersonationDetails, setInnCode } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import { fetchFilterPropertyInfo } from "components/common/Filters/Filters.slice";
import { filterPage } from "components/common/Filters/FiltersHelper";
import GlobalFilterDescription from "components/common/Filters/GlobalFilterDescription";
import GlobalFilters from "components/common/Filters/GlobalFilters";
import { resetPageModifiedFilter } from "components/common/Filters/GlobalFilters.slice";
import SingleSelectFilter from "components/common/SingleSelectFilter";
import Typography from "components/common/Typography";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { ABOVE_PROP_USER_TYPE, accessTypes } from "utils/CommonConstants";
import {
  convertArrayValuesToString,
  formFilterRequestPayload,
  getLoggedInUserDetails,
  getUserTypeTag
} from "utils/CommonFunctions";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import { responseStatusTypes } from "utils/Types";
import { updateRecentlyViewedData } from "../Corp/CorpUser.Slice";
import { setUseEffectState } from "../dashboard/PropertyOverview/PropertyOverview.slice";
import { fetchOwnerDetailsAsync } from "../MyPlans/myplans.slice";
import PlanAssignment from "../PlanAssignment";
import {
  fetchPlansAsync,
  fetchUserDetails,
  fetchUserViewSnapshot,
  impersonateAudit,
  reset,
  setLoader
} from "./IndividualUser.slice";
import PlansList from "./PlansList";
import Snapshot from "./Snapshot";

const IndividualUser = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isMobileScreen } = useContext(ThemeContext);
  const pageName = filterPage.individualUsersDisplay;

  const [searchParams, setSearchParams] = useSearchParams();
  const innCode = searchParams.get("inncode") ?? "";
  const { isConsultant, isCorp, isAbovePropertyUser } = useAppSelector((state) => state.app.loggedInUserDetails);
  const { userDetails, showIndividualUserLoader } = useAppSelector((state) => state.individualUser);
  const userSnapshot = useAppSelector((state) => state.individualUser.userSnapshot);
  const isPlanDeleteSuccessfull = useAppSelector((state: any) => state.formDetails.deletePlan.status);
  const globalFiltersforPage = useAppSelector((state) => state.globalFilters[pageName]);
  const filterRequestPayload = globalFiltersforPage?.modifiedFilter || globalFiltersforPage?.defaultFilter || [];
  const formattedFilterRequestPayload = formFilterRequestPayload(filterRequestPayload, convertArrayValuesToString);

  const [page, setPage] = useState(0);
  const [isOpenPlanAssignmentDialog, setIsOpenPlanAssignmentDialog] = useState(false);

  const inncodeOptions = userDetails ? userDetails.innCodes : [];
  const isLeadershipRoleCategory = getLoggedInUserDetails().roleCategory === "Leadership";

  const searchText = globalFiltersforPage?.searchText;
  useEffect(() => {
    if (isCorp) {
      dispatch(
        updateRecentlyViewedData({
          user: [
            {
              hltId: params?.hltId
            }
          ]
        })
      );
    }
    dispatch(fetchUserDetails({ hltIds: params?.hltId, size: 1 }));
    dispatch(fetchUserViewSnapshot({ hltId: params?.hltId, innCode }));

    return () => {
      dispatch(reset());
    };
  }, []);

  useEffect(() => {
    if (isPlanDeleteSuccessfull === responseStatusTypes.success)
      dispatch(fetchUserViewSnapshot({ hltId: params?.hltId, innCode }));
  }, [isPlanDeleteSuccessfull]);

  const apiCall = async (page?: number | undefined) => {
    const payload = {
      includeOwners: page === 0,
      searchedHltId: params?.hltId,
      innCode,
      searchText,
      ...formattedFilterRequestPayload
    };

    await dispatch(fetchPlansAsync({ payload, page })).then((res: any) => {
      if (page === 0 && res?.payload?.meta?.owners?.length) {
        dispatch(
          fetchOwnerDetailsAsync({ size: res?.payload?.meta?.owners?.length, value: res?.payload?.meta?.owners })
        );
        dispatch(setLoader(false));
      }
    });
  };

  useEffect(() => {
    if (page > 0) apiCall(page);
  }, [page]);

  useEffect(() => {
    setPage(0);
    apiCall(0);
  }, [filterRequestPayload, innCode, searchText]);

  const fetchMore = () => {
    setPage((pageNo) => pageNo + 1);
  };

  const handleCloseDialog = () => {
    setIsOpenPlanAssignmentDialog(false);
  };

  const handleImpersonateUserClick = async (member: any) => {
    const isAbovePropertyUser = String(member?.lobbyUserType).toLowerCase() === ABOVE_PROP_USER_TYPE;
    dispatch(
      setImpersonationDetails({
        isImpersonated: true,
        navigateToURL: `userview/${userDetails["hltId"]}?inncode=${innCode}`,
        userDetails: { ...member, isAbovePropertyUser, isConsultant }
      })
    );
    await dispatch(impersonateAudit({}));
    dispatch(setInnCode(innCode));
    dispatch(fetchFilterPropertyInfo({ innCodesValue: member?.innCodes, isUserAssociated: true }));
    navigate("/");
  };

  const getActionButtons = () => {
    return (
      !isLeadershipRoleCategory && (
        <Stack
          direction={isMobileScreen ? "row-reverse" : "row"}
          alignItems="center"
          justifyContent={isMobileScreen ? "center" : "flex-start"}
          spacing={3}
          sx={{ width: isMobileScreen ? "100%" : "auto" }}
        >
          {isAbovePropertyUser && (
            <Box
              sx={{ width: "50%" }}
              component={Link}
              to={`/userDashboard?member=${params?.hltId}&inncode=${innCode}`}
            >
              <Button variant="secondary" label="View Dashboard" sx={{ width: "100%" }} />
            </Box>
          )}
          <Button
            label="Assign Plan"
            accessType={accessTypes.assign}
            sx={{ width: "40%" }}
            onClick={() => setIsOpenPlanAssignmentDialog(true)}
          />

          {isCorp && (
            <Button
              startIcon={<ImpersonateIcon />}
              onClick={() => {
                handleImpersonateUserClick(userDetails);
                dispatch(setUseEffectState(true));
                dispatch(resetPageModifiedFilter({ pageName: pageName }));
              }}
              variant="secondary"
              label="Impersonate user"
            />
          )}
        </Stack>
      )
    );
  };

  return (
    <>
      {showIndividualUserLoader ? (
        <></>
      ) : (
        <Box sx={{ my: "24px" }}>
          {!isLeadershipRoleCategory && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ my: "24px" }}>
              <Typography variant="body2" color={tokens.Text.General.Secondary}>
                Inncode Selected
              </Typography>
              <Box flex={1} sx={{ maxWidth: "160px" }}>
                <SingleSelectFilter
                  showTextField={false}
                  label="Inncode"
                  showSelectedOption
                  options={inncodeOptions}
                  noOptionsMessage={"No inncode(s) found"}
                  selectedOption={innCode}
                  onSubmit={(val: any) => {
                    dispatch(fetchUserViewSnapshot({ hltId: params?.hltId, innCode: val }));
                    setSearchParams({ inncode: val }, { replace: true });
                  }}
                />
              </Box>
            </Stack>
          )}
          <Stack
            direction={isMobileScreen ? "column" : "row"}
            alignItems={isMobileScreen ? "flex-start" : "center"}
            spacing={2}
          >
            <Stack flex="1" spacing={2}>
              <Typography variant="h4" showBold color={tokens.Text.General.PrimaryHeadline}>
                {getUserNameWithConditionalCheck(
                  userDetails?.displayName,
                  userDetails?.firstName,
                  userDetails?.lastName
                )}
              </Typography>
              <Stack direction="row" alignItems="center">
                <Typography showBold variant="body2" color={tokens.Text.General.PrimaryHeadline}>
                  User Type
                </Typography>
                <Typography variant="body2">: {getUserTypeTag(userDetails.lobbyUserType)}</Typography>
              </Stack>

              <Stack
                direction={isMobileScreen ? "column" : "row"}
                alignItems={isMobileScreen ? "flex-start" : "center"}
                spacing={1}
                sx={{ color: tokens.Text.General.PrimaryHeadline }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    pr: "6px",
                    borderRight: isMobileScreen ? "none" : `1px solid ${tokens.Border.General.Secondary}`
                  }}
                >
                  <Typography showBold variant="body2">
                    Role
                  </Typography>
                  <Typography variant="body2">: {userDetails.roles?.join(", ")}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center">
                  <Typography showBold variant="body2">
                    Last Login
                  </Typography>
                  <Typography variant="body2">
                    : {userDetails.lastLoggedInDate && moment(userDetails.lastLoggedInDate).format("DD MMM, yyyy")}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
            {getActionButtons()}
          </Stack>

          <Snapshot
            planSnapshotData={userSnapshot.USER_PROPERTY_PLAN_SNAPSHOT[0]}
            actionSnapshotData={userSnapshot.USER_PROPERTY_CARDS_SNAPSHOT[0]}
          />

          <Typography
            variant="h4"
            showBold
            color={tokens.Text.General.PrimaryHeadline}
            sx={{
              my: "24px"
            }}
          >
            Plans
          </Typography>

          <GlobalFilters isPreDefinedNav={true} isSearchBarVisible page={pageName} searchedText={searchText} />
          <GlobalFilterDescription page={pageName} bgColor={tokens.Background.General.Primary} />

          <PlansList fetchMore={fetchMore} />
          {isOpenPlanAssignmentDialog && (
            <PlanAssignment
              open={isOpenPlanAssignmentDialog}
              handleCloseDialog={handleCloseDialog}
              flow="second"
              inncodes={innCode}
              users={params?.hltId}
              userSelectionFlow="fourth"
              shouldHideTargetingFilters
            />
          )}
        </Box>
      )}
    </>
  );
};
export default IndividualUser;
